import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { EmailLink } from "./PolicyLayout";

function Privacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      intro="This policy explains how tunrisefield handles personal information when you browse, place an order, or contact us."
    >
      <h2>1. Scope and responsibility</h2>
      <p>
        This policy applies to personal information handled through this online store and related
        customer support. “tunrisefield,” “we,” “us,” and “our” refer to the store operator. For
        privacy questions or requests, email <EmailLink subject="Privacy request" />.
      </p>

      <h2>2. Information we collect</h2>
      <ul>
        <li><strong>Order and contact data:</strong> name, email address, delivery and billing details, products, quantities, order value, and order history.</li>
        <li><strong>Payment data:</strong> payment status, transaction reference, payment method type, and limited fraud-prevention signals. Full card details are entered on the payment provider’s page and are not intentionally stored by this website.</li>
        <li><strong>Device and network data:</strong> IP address, browser and device type, referring page, timestamps, security logs, and basic interactions necessary to operate and protect the store.</li>
        <li><strong>Support data:</strong> messages, attachments, return reasons, photographs of damaged goods, and any information you choose to provide.</li>
        <li><strong>Local cart data:</strong> product identifiers, quantity, size, and color saved in your browser’s local storage.</li>
      </ul>
      <p>We ask that you do not send sensitive personal information that is not needed to resolve your request.</p>

      <h2>3. How we collect information</h2>
      <p>
        We collect information directly from you, automatically from your browser and our service
        infrastructure, and from providers involved in payment, fraud prevention, hosting,
        delivery, and order support. We may combine these records when reasonably necessary to
        fulfill or protect a transaction.
      </p>

      <h2>4. Why we use information</h2>
      <ul>
        <li>to provide the cart, checkout, payment, delivery, returns, and customer support;</li>
        <li>to confirm transactions and communicate material order updates;</li>
        <li>to detect fraud, abuse, security incidents, and technical problems;</li>
        <li>to keep business, tax, accounting, warranty, and dispute records;</li>
        <li>to comply with lawful requests and enforce our policies; and</li>
        <li>to improve the reliability, usability, and product selection of the store.</li>
      </ul>

      <h2>5. Legal grounds</h2>
      <p>
        Where a legal basis is required, we rely on performance of a contract or steps requested
        before a contract, compliance with legal obligations, our legitimate interests in operating
        and securing the store, and consent where the law requires it. You may withdraw consent at
        any time, without affecting earlier lawful processing.
      </p>

      <h2>6. When we disclose information</h2>
      <p>We disclose only what is reasonably necessary to:</p>
      <ul>
        <li>payment, fraud-prevention, hosting, IT, email, fulfillment, and delivery providers;</li>
        <li>professional advisers, insurers, auditors, and transaction counterparties under appropriate confidentiality duties;</li>
        <li>public authorities or other parties when disclosure is legally required or reasonably necessary to protect rights, safety, and the integrity of the store; and</li>
        <li>a successor in a merger, financing, reorganization, or sale, subject to applicable law.</li>
      </ul>
      <p>
        We do not sell personal information for money. We do not knowingly use personal information
        for cross-context behavioral advertising on this website. See <Link to="/data-choices">Privacy Choices</Link> for request options.
      </p>

      <h2>7. International processing</h2>
      <p>
        Providers may process information outside your country. Where applicable law requires it,
        we use recognized transfer safeguards or another valid legal mechanism. Local laws in a
        destination country may differ from those where you live.
      </p>

      <h2>8. Retention</h2>
      <p>
        We retain information only for as long as reasonably necessary for the purposes above.
        Transaction records may be kept for the period required by tax, accounting, payment,
        chargeback, consumer-protection, and recordkeeping rules. Support and security records are
        removed or de-identified when they are no longer needed, subject to legal holds.
      </p>

      <h2>9. Security</h2>
      <p>
        We use reasonable administrative and technical safeguards appropriate to the nature of the
        information. Payment details are submitted on a payment provider’s hosted page. No method of
        transmission or storage is completely secure, so we cannot promise absolute security. If you
        believe your order or information has been compromised, contact us promptly.
      </p>

      <h2>10. Your rights and choices</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, delete, restrict, object,
        receive a portable copy, withdraw consent, opt out of certain disclosures, or appeal a request
        decision. We will not unlawfully discriminate against you for exercising a privacy right. See
        our <Link to="/data-choices">Privacy Choices page</Link> for instructions.
      </p>

      <h2>11. Children</h2>
      <p>
        The store is intended for people who can lawfully enter a purchase contract. We do not
        knowingly collect personal information from children through accounts or child-directed
        services. A parent or guardian may contact us to request review or deletion of information
        submitted by a child.
      </p>

      <h2>12. Third-party services</h2>
      <p>
        External payment or delivery services have their own privacy notices and act under their own
        legal responsibilities for some processing. This policy does not control independent third-party
        sites reached through a link.
      </p>

      <h2>13. Changes and contact</h2>
      <p>
        We may update this policy when our services, providers, or legal obligations change. Material
        changes will be identified by a new revision date and, where required, an additional notice.
        Contact <EmailLink subject="Privacy policy question" /> with privacy questions.
      </p>
    </PolicyLayout>
  );
}

export default Privacy;
